import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'i18next';

import { ChevronDownSmall } from '@peacecover/icon';
import { usePhotoCardMarketQuery } from './hooks/usePhotoCardMarketQuery';
import { useTabFromQueryParam } from '../hooks/useTabFromQueryParam';
import { useClickOutside } from './hooks/useCloseModal';
import { usePhotoCardMarketPopupStore } from './store/usePhotoCardMarketPopupStore';
import { calculateTabType } from './utils/calculateTabType';
import NewMyPhotoCardsMarketHeader from './components/NewMyPhotoCardsMarketHeader';
import TabLineFill from '../components/tab/TabLineFill';
import { TabSegment } from '../components/tab/TabSegment';
import FilterMenuDropdown from './components/FilterMenuDropdown';
import BeforeReviewPopup from './components/BeforeReviewPopup';
import TradingPhotoCardSkeleton from './components/TradingPhotoCardSkeleton';
import TradingPhotoCardItemList from './components/TradingPhotoCardItemList';
import WriteReviewPopup from './components/WriteReviewPopup';
import ExchangePhotoCardItemList from './components/ExchangePhotoCardItemList';
import type { SaleTradingPhotoCardType } from './types/MarketCardType';
import { FILTERED_MENU } from './constants/filteredMenu';

const NewMyPhotoCardsMarket = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { activeTab, goToTab } = useTabFromQueryParam();

  const [selectedMenu, setSelectedMenu] = useState(FILTERED_MENU.ALL);
  const [subTabIndex, setSubTabIndex] = useState(0);
  const { dropdownRef, isMenuDropdownOpen, setIsMenuDropdownOpen } = useClickOutside();

  const { isBeforeReviewPopupOpen, isWriteReviewPopupOpen } = usePhotoCardMarketPopupStore();

  const { data, isLoading } = usePhotoCardMarketQuery(calculateTabType(activeTab, subTabIndex));

  const filteredPhotoCards = data?.filter((tradingPhotoCard: SaleTradingPhotoCardType) => {
    if (selectedMenu === FILTERED_MENU.ALL) {
      return true;
    }
    if (selectedMenu === FILTERED_MENU.ONGOING) {
      return tradingPhotoCard.status === 'pending' || tradingPhotoCard.status === 'ongoing';
    }
    return (
      tradingPhotoCard.status === 'booked' ||
      tradingPhotoCard.status === 'sold_out' ||
      tradingPhotoCard.status === 'canceled'
    );
  });

  console.log(filteredPhotoCards);

  return (
    <div className="min-h-screen bg-pure-white dark:bg-background-dark pt-[calc(env(safe-area-inset-top))] pb-[calc(30px+env(safe-area-inset-bottom))]">
      <NewMyPhotoCardsMarketHeader />
      <div className="h-[48px] mt-[44px]">
        <TabLineFill
          tabIndex={activeTab}
          segments={[
            t('NewTradingPhotoCardMarket.tabSaleAndExchange'),
            t('NewTradingPhotoCardMarket.tabExchange')
          ]}
          onTabChange={(index) => {
            goToTab(index);
            navigate(`${location.pathname}?tabIndex=${index}`, { replace: true });
          }}
        />
      </div>
      <div className="w-full mt-[18px] px-[24px] h-[52px] py-[4px]">
        {activeTab === 0 ? (
          <TabSegment
            tabIndex={subTabIndex}
            segments={[
              t('NewTradingPhotoCardMarket.tabSaleHistory'),
              t('NewTradingPhotoCardMarket.tabExchangeHistory')
            ]}
            onTabChange={setSubTabIndex}
          />
        ) : (
          <TabSegment
            tabIndex={subTabIndex}
            segments={[
              t('NewTradingPhotoCardMarket.tabFirstStart'),
              t('NewTradingPhotoCardMarket.tabSecondStart')
            ]}
            onTabChange={setSubTabIndex}
          />
        )}
      </div>
      <MarketTextInfo />
      <div className="flex flex-col items-center px-[24px]">
        <div className="flex justify-between w-full mt-[20px] mb-[8px]">
          <p className="text-[15px] font-bold text-text-main-lv2">
            {t('NewTradingPhotoCardMarket.totalCount', { count: data?.length ?? 0 })}
          </p>
          <div>
            <div className="relative" ref={dropdownRef}>
              <button
                className="flex gap-[4px] items-center"
                type="button"
                onClick={() => setIsMenuDropdownOpen(!isMenuDropdownOpen)}
              >
                <p className="text-[13px] text-text-main-lv3">{selectedMenu}</p>
                <ChevronDownSmall size={20} className="text-text-main-lv3" />
              </button>
              {isMenuDropdownOpen && (
                <FilterMenuDropdown
                  selectedMenu={selectedMenu}
                  setSelectedMenu={setSelectedMenu}
                  setIsMenuDropdownOpen={setIsMenuDropdownOpen}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-[12px] mx-[24px]">
        {isLoading ? (
          <TradingPhotoCardSkeleton />
        ) : activeTab === 0 ? (
          <TradingPhotoCardItemList
            filteredPhotoCards={filteredPhotoCards}
            subTabIndex={subTabIndex}
          />
        ) : (
          <ExchangePhotoCardItemList filteredPhotoCards={filteredPhotoCards} />
        )}
      </div>
      {isBeforeReviewPopupOpen && <BeforeReviewPopup />}
      {isWriteReviewPopupOpen && <WriteReviewPopup />}
    </div>
  );
};

export default NewMyPhotoCardsMarket;

const MarketTextInfo = () => {
  return (
    <div className="flex flex-col gap-[6px] ml-[24px] mt-[12px] leading-none">
      <div className="flex items-center gap-[6px]">
        <div className="rounded-full w-[3px] h-[3px] bg-text-main-lv4 items-center" />
        <p className="text-[13px] text-text-main-lv4">
          {t('NewTradingPhotoCardMarket.chatHistoryInfo')}
        </p>
      </div>
      <div className="flex items-center gap-[6px]">
        <div className="rounded-full w-[3px] h-[3px] bg-text-main-lv4 items-center" />
        <p className="text-[13px] text-text-main-lv4">
          {t('NewTradingPhotoCardMarket.reviewInfo')}
        </p>
      </div>
    </div>
  );
};
